import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { MediaChannel } from '@nativewaves/nw-cloud-sdk/media'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useMediaChannelCreateMutation, useMediaChannelDeleteMutation } from 'hooks/mutations/publishing'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<MediaChannel>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createMediaChannel, ...mediaChannelCreateMutation } = useMediaChannelCreateMutation()
  const { mutate: deleteMediaChannel } = useMediaChannelDeleteMutation()

  const handleDuplicateMediaChannel = useCallback(() => {
    createMediaChannel(
      { mediaChannelInsert: row.original },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createMediaChannel, onClose, row.original])

  const handleDeleteMediaChannel = useCallback(() => {
    onClose()

    deleteMediaChannel(
      { channelId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:MediaChannel.nameDelete', { name: row.original.name }), { variant: 'success' }),
      },
    )
  }, [deleteMediaChannel, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateMediaChannel} disabled={mediaChannelCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {mediaChannelCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteMediaChannel} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
